import { useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { readCsrfCookie } from "./csrfCookie";

function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "something went wrong";
}

/**
 * Self-service: set or update the caller's OWN recovery email —
 * self-service forgot-password (ForgotPassword.tsx) only works for an
 * account once this exists. There is no separate "edit" mode: the field
 * always shows the current value (or blank) and submitting just saves
 * whatever is there, the same shape PATCH /v1/auth/me itself has.
 */
export function MyAccountPanel({ currentEmail }: { currentEmail: string | null }) {
  const [email, setEmail] = useState(currentEmail ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const csrfToken = readCsrfCookie(document.cookie);
      const result = await api.setMyEmail(csrfToken, email);
      setEmail(result.email);
      setSaved(result.email);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section aria-labelledby="my-account-heading" className="card">
      <h2 id="my-account-heading">My account</h2>
      <p className="card__note">
        Your own recovery email. Required before &ldquo;Forgot password?&rdquo; on the sign-in
        screen can do anything for this account — it is never shown to or editable by anyone else.
      </p>
      <form className="invites__form" onSubmit={(event) => void handleSubmit(event)}>
        <label className="field">
          <span>Email</span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
            required
          />
        </label>
        <button type="submit" disabled={submitting}>
          {submitting ? "Saving…" : "Save"}
        </button>
      </form>
      {saved ? (
        <p className="card__note" role="status">
          Saved — reset links will be sent to {saved}.
        </p>
      ) : null}
      {error ? <p className="card__error">{error}</p> : null}
    </section>
  );
}
